/**
 * View Proxy Client — generic reader for all /v1/views/* endpoints.
 *
 * ADR-056: All reads go through view proxy. One response shape.
 * Column contracts: read_doc(id="d7f9814f-14fa-4f7b-b8a4-d3d17b373803")
 *
 * Usage:
 *   const { data, total, meta } = await viewGet<Appointment>('appointments', { status: 'planned' })
 */

import { get } from './serviceLayerClient'
import { assertImpersonationScope } from './impersonationGuard'

export interface ViewMeta {
  view: string
  source: string
  ownership: string
  cache_ttl: number
  cache_hit?: boolean
  degraded?: boolean
  served_at: string
  request_id?: string
}

export interface ViewResponse<T> {
  data: T[]
  total: number
  meta: ViewMeta
}

export interface ViewGetOptions {
  /** Filter params as key-value pairs → query string */
  filters?: Record<string, string | number | boolean | null | undefined>
  /** Pagination */
  limit?: number
  offset?: number
  /** Sort */
  order?: string
  /**
   * Optional cancellation signal — typically `queryFn`'s React Query
   * signal. Threaded through to the underlying `serviceFetch` so
   * unmount-cancellations propagate before any timeout can orphan
   * (FR-SL-CLIENT-ABORT-LEAK pattern, see httpAbort.ts).
   */
  signal?: AbortSignal
}

function buildQueryString(opts: ViewGetOptions): string {
  const params = new URLSearchParams()
  if (opts.filters) {
    for (const [k, v] of Object.entries(opts.filters)) {
      if (v != null && v !== '') params.set(k, String(v))
    }
  }
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.offset != null) params.set('offset', String(opts.offset))
  if (opts.order) params.set('order', opts.order)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Fetch from a view proxy endpoint.
 * Returns typed ViewResponse<T> with data array, total count, and meta.
 *
 * If the response has the old {success, data} shape (legacy endpoint),
 * it adapts gracefully — data is wrapped, meta is synthesized.
 */
export async function viewGet<T>(
  viewName: string,
  opts: ViewGetOptions = {},
): Promise<ViewResponse<T>> {
  // D-26 R2: prevent impersonation scope leaks at the FE boundary. If
  // the view slug is user-scoped and no scope filter is present, refuse
  // the request rather than serve stale Commander-scoped data.
  assertImpersonationScope(viewName, Object.keys(opts.filters ?? {}))

  const path = `/v1/views/${viewName}${buildQueryString(opts)}`
  const res = await get<unknown>(path, { signal: opts.signal })

  const body = res.data as Record<string, unknown>

  // View proxy shape: { data: [...], total: N, meta: {...} }
  if (Array.isArray(body?.data) && body?.meta) {
    return body as unknown as ViewResponse<T>
  }

  // View proxy shape without meta (shouldn't happen but defensive)
  if (Array.isArray(body?.data)) {
    return {
      data: body.data as T[],
      total: (body.total as number) ?? (body.data as T[]).length,
      meta: {
        view: viewName,
        source: 'unknown',
        ownership: 'unknown',
        cache_ttl: 0,
        served_at: new Date().toISOString(),
      },
    }
  }

  // Bare array (legacy fallback)
  if (Array.isArray(body)) {
    return {
      data: body as T[],
      total: (body as T[]).length,
      meta: {
        view: viewName,
        source: 'legacy',
        ownership: 'unknown',
        cache_ttl: 0,
        served_at: new Date().toISOString(),
      },
    }
  }

  // Single object — wrap in array
  return {
    data: body ? [body as T] : [],
    total: body ? 1 : 0,
    meta: {
      view: viewName,
      source: 'legacy',
      ownership: 'unknown',
      cache_ttl: 0,
      served_at: new Date().toISOString(),
    },
  }
}

/**
 * Check if the view response indicates degraded data.
 * Show a yellow banner if true.
 */
export function isViewDegraded(meta: ViewMeta): boolean {
  return meta.degraded === true
}
